import React from "react";

/**
 * Shared pagination control.
 *
 * Layout stability: the controls row is fixed-width on sm+ and the number
 * cluster always renders exactly 7 cells (numbers, ellipses, or invisible
 * placeholders when totalPages ≤ 7). Prev and Next stay pinned to the row
 * edges and the cluster never changes shape between pages — only the digits
 * inside the cells change.
 *
 * Responsive:
 *  - phone: "Showing X–Y of Z" sits above the controls row. The 7-cell number
 *    track is replaced by a compact "Page X of Y" indicator so Prev and Next
 *    don't overlap on narrow viewports.
 *  - sm+: single row with the label on the left and the full 7-cell track
 *    between Prev and Next.
 *
 * Usage:
 *   <Pagination
 *     page={page}
 *     totalPages={totalPages}
 *     onPageChange={setPage}
 *     totalItems={totalItems}
 *     perPage={PAGE_SIZE}
 *     label="rows"
 *   />
 */
const Pagination = ({ page, totalPages, onPageChange, totalItems, perPage, label = "items", alwaysShow = false }) => {
  // Hide the footer on single-page sets unless the caller opts in via alwaysShow
  // (e.g. Local Intelligence's answer panel keeps its bottom edge stable for 1 page).
  if (totalPages <= 1 && !alwaysShow) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalItems);

  // Always render 7 slots so the cluster has a fixed visual footprint across
  // every page. Three shapes (totalPages > 7):
  //   - near start  : [1, 2, 3, 4, 5, …, totalPages]
  //   - near end    : [1, …, n-4, n-3, n-2, n-1, n]
  //   - middle      : [1, …, page-1, page, page+1, …, totalPages]
  // When totalPages ≤ 7, show 1..N padded to 7 with empty placeholders so the
  // cluster width stays identical to the >7 case.
  const SLOTS = 7;
  let pages;
  if (totalPages <= SLOTS) {
    // No reflow risk when total fits — render real pages only, skip the
    // invisible gap cells (otherwise short pagers float in empty space).
    pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else if (page <= 4) {
    pages = [1, 2, 3, 4, 5, "...", totalPages];
  } else if (page >= totalPages - 3) {
    pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  } else {
    pages = [1, "...", page - 1, page, page + 1, "...", totalPages];
  }

  const edgeBtn =
    "shrink-0 w-[78px] h-7 text-[13px] rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mt-4">
      <span className="text-[13px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
        Showing {from}–{to} of {totalItems.toLocaleString()} {label}
      </span>
      <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={edgeBtn}
        >
          Previous
        </button>

        {/* Phone: compact "Page X of Y" so Prev and Next have room without overlap */}
        <span className="sm:hidden text-[13px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
          Page {page} of {totalPages}
        </span>

        {/* sm+: fixed 7-cell number track */}
        <div className="hidden sm:flex items-center justify-center gap-1">
          {pages.map((p, idx) => {
            if (p === "gap") {
              return <span key={`g${idx}`} aria-hidden="true" className="w-7 h-7 shrink-0" />;
            }
            if (p === "...") {
              return (
                <span
                  key={`e${idx}`}
                  className="w-7 h-7 shrink-0 inline-flex items-center justify-center text-[13px] text-gray-400 select-none"
                >
                  …
                </span>
              );
            }
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`shrink-0 w-7 h-7 text-[13px] rounded-md transition-colors ${
                  p === page
                    ? "bg-[#32B1FF] text-white font-bold"
                    : "bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {p}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className={edgeBtn}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
